import CustomModal from '@components/CustomModal';
import GridListSubheader from '@components/GridListSubheader';
import DeleteIntegrationModal from '@components/modals/DeleteIntegrationModal';
import GenerateStructureIntegrationModal from '@components/modals/GenerateStructureModal';
import GeneratingStructureLoadingModal from '@components/modals/GeneratingStructureLoadingModal';
import WolfTooltip from '@components/ui/WolfTooltip';
import { graphQlClient } from '@config/graphqlClient';
import { getSessionUuid, getTokenLocalStorage, getTokenSessionStorage } from '@config/storage';

import IntegrationsGraphQL from '@graphql/integration.queries';
import UserGraphQL from '@graphql/user.queries';
import { useLoader } from '@hooks/useLoader';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';

import {
  deleteIntegrationRequest,
  getIntegrationRequest,
  Integration,
  IntegrationData,
  IntegrationProvider,
  IntegrationStatus,
  IntegrationType,
} from '@models/integration.model';
import { getAssignableUsersRequest, User, UserRoles } from '@models/user.model';
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { RootState } from '@redux/store';
import { useEffect, useState } from 'react';

const IntegrationList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { setLoading } = useLoader();
  //TODO: Get all possible integrations from backend

  const { isImpersonating } = useAppSelector((state: RootState) => state.impersonate);
  const { rememberMe } = useAppSelector((state: RootState) => state.user);
  const { organizationId } = useAppSelector((state: RootState) => state.organization);
  const { role } = useAppSelector((state: RootState) => state.user);
  const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();

  const localeIntegrations = useTranslation('integrations');
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');

  const possibleIntegrations: Integration[] = [
    {
      integrationType: IntegrationType.CALENDAR,
      integrationProvider: IntegrationProvider.GOOGLE,
      description: 'Sync your calendar with your account',
      status: IntegrationStatus.NOT_ACTIVATED,
      link: process.env.REACT_APP_BACKEND_URL + 'auth/linkCalendar?token=' + token,
    },
    {
      integrationType: IntegrationType.STORAGE,
      integrationProvider: IntegrationProvider.GOOGLE,
      description: 'Sync your storage with your account with Google Drive',
      status: IntegrationStatus.NOT_ACTIVATED,
      link: process.env.REACT_APP_BACKEND_URL + 'auth/' + 'linkDrive?token=' + token,
    },
    {
      integrationType: IntegrationType.MESSAGING,
      integrationProvider: IntegrationProvider.SLACK,
      description: localeIntegrations['slackDescription'] || 'Send notifications to users via Slack DM',
      status: IntegrationStatus.NOT_ACTIVATED,
    },
  ];

  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [generatingStructure, setGeneratingStructure] = useState(false);
  const [generatingIntegrationType, setGeneratingIntegrationType] = useState<IntegrationType>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration>();
  const [refresh, setRefresh] = useState(false);

  const getUserNameById = (userId: string | undefined): string | null => {
    if (!userId) return null;
    const user = users.find((u) => u.userId === userId);
    if (!user) return null;
    return user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email || null;
  };

  const getRelativeTime = (dateString: string | undefined): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return localeIntegrations['justNow'];
    if (diffMins < 60) return localeIntegrations['minutesAgo'].replace('{count}', String(diffMins));
    if (diffHours < 24) return localeIntegrations['hoursAgo'].replace('{count}', String(diffHours));
    return localeIntegrations['daysAgo'].replace('{count}', String(diffDays));
  };

  const getHealthStatusInfo = (
    healthStatus: IntegrationData['healthStatus'],
  ): { label: string; color: 'success' | 'error' | 'default'; description: string } => {
    switch (healthStatus) {
      case 'HEALTHY':
        return {
          label: localeIntegrations['healthHealthy'],
          color: 'success',
          description: localeIntegrations['healthHealthyDescription'],
        };
      case 'UNHEALTHY':
        return {
          label: localeIntegrations['healthUnhealthy'],
          color: 'error',
          description: localeIntegrations['healthUnhealthyDescription'],
        };
      default:
        return {
          label: localeIntegrations['healthUnknown'],
          color: 'default',
          description: localeIntegrations['healthUnknownDescription'],
        };
    }
  };

  useEffect(() => {
    const handleFocus = () => {
      if (refresh) {
        getIntegrations(true);
        setRefresh(false);
      }
    };

    getIntegrations();
    fetchUsers();
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const fetchUsers = async () => {
    try {
      const data: getAssignableUsersRequest = await graphQlClient.request(
        UserGraphQL.queries.getAssignableUsers,
      );
      setUsers(data?.getAssignableUsers || []);
    } catch (e) {
      // Silently fail - users list is optional for display
    }
  };

  const connectSlack = async () => {
    if (isImpersonating) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['whileImpersonatingError'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}integrations/slack/oauth?organizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Organization-Id': organizationId || '',
            'Session-Uuid': getSessionUuid(),
          },
        },
      );
      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      dispatch(
        appendActionMessage({
          message: localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const getIntegrations = async (wait: boolean = false) => {
    try {
      setLoading(true);
      if (wait) {
        await new Promise(resolve => setTimeout(resolve, 5000));
      }
      const data: getIntegrationRequest = await graphQlClient.request(IntegrationsGraphQL.queries.getIntegrations);
      setIntegrations(
        possibleIntegrations.map((possible) => {
          const matchingInt = data?.getIntegrations.find(
            (existing) =>
              possible.integrationType === existing.integrationType &&
              possible.integrationProvider === existing.integrationProvider,
          );

          if (matchingInt) {
            return {
              ...possible,
              status: matchingInt.status,
              integrationId: matchingInt.integrationId,
              connectedByUserId: matchingInt.connectedByUserId,
              connectedAt: matchingInt.connectedAt,
              externalAccountEmail: matchingInt.externalAccountEmail,
              externalAccountId: matchingInt.externalAccountId,
              integrationData: matchingInt.integrationData,
            };
          } else {
            return possible;
          }
        }),
      );
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteIntegration = async (id: string) => {
    try {
      const data: deleteIntegrationRequest = await graphQlClient.request(
        IntegrationsGraphQL.mutations.deleteIntegration,
        { id },
      );
      if (data?.deleteIntegration) {
        getIntegrations();
        dispatch(
          appendActionMessage({
            message: localeActionMessages['integrationDeletedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const generateStructure = async (integration: Integration | undefined) => {
    if (!integration) {
      return;
    }
    setGeneratingStructure(true);
    setGeneratingIntegrationType(integration.integrationType);

    const variables = {
      integrationType: integration.integrationType,
      integrationProvider: integration.integrationProvider,
      integrationId: integration.integrationId,
    };

    try {
      await graphQlClient.request(IntegrationsGraphQL.mutations.generateStructure, {
        integration: variables,
      });
      setGeneratingStructure(false);
      setGeneratingIntegrationType(undefined);
      getIntegrations();
    } catch (e: any) {
      setGeneratingStructure(false);
      setGeneratingIntegrationType(undefined);
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const handleShowDeleteModal = (integration: Integration) => {
    if (isImpersonating) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['whileImpersonatingError'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }
    setSelectedIntegration(integration);
    setShowDeleteModal(true);
  };

  const handleShowGenerateModal = (integration: Integration) => {
    if (isImpersonating) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['whileImpersonatingError'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }
    setSelectedIntegration(integration);
    setShowGenerateModal(true);
  };

  const cancelModal = () => {
    setShowDeleteModal(false);
    setShowGenerateModal(false);
    setSelectedIntegration(undefined);
  };

  const handleConfirmDelete = () => {
    deleteIntegration(selectedIntegration?.integrationId || '');
    cancelModal();
  };

  const handleConfirmStructureGenerate = () => {
    generateStructure(selectedIntegration);
    cancelModal();
  };

  const refreshOnFocus = () => {
    setRefresh(true);
    dispatch(
      appendActionMessage({
        message: localeIntegrations['refreshAfterOAuthMessage'],
        type: SnackType.DARK,
        duration: 30000
      }),
    );
  };

  return (
    <Box className="w-full">
      <Box className="sticky top-0 z-30 bg-backgroundUI">
        <GridListSubheader
          title={localeIntegrations['title']}
          buttonText={null}
          isGridView={false}
          onToggleView={() => {}}
          onDrawerOpen={() => {}}
          icon="integration"
          showFavGrid={false}
        />
      </Box>{' '}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">{localeIntegrations['type']}</TableCell>
              <TableCell align="center">{localeIntegrations['provider']}</TableCell>
              <TableCell align="center">{localeIntegrations['status']}</TableCell>
              <TableCell align="center">{localeIntegrations['connectedAccount']}</TableCell>
              <TableCell align="center">{localeIntegrations['healthStatus']}</TableCell>
              <TableCell align="center">{localeCommon['action']}</TableCell>
              <TableCell align="center">{localeCommon['delete']}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {integrations.map((integration, index: number) => (
              <WolfTooltip
                key={index}
                title={integration.integrationProvider === IntegrationProvider.SLACK ? localeIntegrations['slackInternalOnlyTooltip'] : ''}
                placement="bottom"
                arrow
              >
              <TableRow>
                <TableCell align="center">
                  {integration.integrationType?.charAt(0).toUpperCase() +
                    integration.integrationType?.slice(1).toLowerCase()}
                </TableCell>
                <TableCell align="center">
                  {integration.integrationProvider?.charAt(0).toUpperCase() +
                    '' +
                    integration.integrationProvider?.slice(1).toLowerCase()}
                </TableCell>
                <TableCell align="center">
                  {integration.status?.charAt(0).toUpperCase() +
                    '' +
                    integration.status?.slice(1).toLowerCase().replace('_', ' ')}
                </TableCell>
                <TableCell align="center">
                  {(integration.status === IntegrationStatus.ENABLED ||
                    integration.status === IntegrationStatus.GENERATING ||
                    integration.status === IntegrationStatus.GENERATED ||
                    integration.status === IntegrationStatus.FAILED) &&
                    integration.externalAccountEmail && (
                      <Box>
                        <Typography variant="body2">
                          {localeIntegrations['connectedAs'].replace('{email}', integration.externalAccountEmail)}
                        </Typography>
                        {integration.connectedAt && (
                          <Typography variant="caption" color="textSecondary">
                            {localeIntegrations['connectedOn'].replace(
                              '{date}',
                              new Date(integration.connectedAt).toLocaleDateString(),
                            )}
                            {getUserNameById(integration.connectedByUserId) &&
                              ` ${localeIntegrations['connectedBy'].replace(
                                '{name}',
                                getUserNameById(integration.connectedByUserId) || '',
                              )}`}
                          </Typography>
                        )}
                      </Box>
                    )}
                </TableCell>
                <TableCell align="center">
                  {(integration.status === IntegrationStatus.ENABLED ||
                    integration.status === IntegrationStatus.GENERATING ||
                    integration.status === IntegrationStatus.GENERATED) && (
                    <Box>
                      <Tooltip title={getHealthStatusInfo(integration.integrationData?.healthStatus).description}>
                        <Chip
                          label={getHealthStatusInfo(integration.integrationData?.healthStatus).label}
                          color={getHealthStatusInfo(integration.integrationData?.healthStatus).color}
                          size="small"
                        />
                      </Tooltip>
                      {integration.integrationData?.errorCount !== undefined && integration.integrationData.errorCount > 0 && (
                        <Typography variant="caption" display="block" color="error">
                          {localeIntegrations['failedAttempts'].replace('{count}', String(integration.integrationData.errorCount))}
                        </Typography>
                      )}
                      {integration.integrationData?.lastHealthCheck && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {localeIntegrations['lastChecked'].replace('{time}', getRelativeTime(integration.integrationData.lastHealthCheck))}
                        </Typography>
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell align="center">
                  {/* Google integrations with static link */}
                  {integration.link &&
                    !(
                      integration.status === IntegrationStatus.ENABLED ||
                      integration.status === IntegrationStatus.GENERATED ||
                      integration.status === IntegrationStatus.GENERATING ||
                      integration.status === IntegrationStatus.FAILED
                    ) &&
                    (isImpersonating ? (
                      <Typography>{localeActionMessages['whileImpersonatingError']}</Typography>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        href={integration.link}
                        onClick={refreshOnFocus}
                        target="_blank">
                        {localeIntegrations['clickToEnable']}
                      </Button>
                    ))}
                  {/* Slack OAuth flow (no static link) */}
                  {integration.integrationProvider === IntegrationProvider.SLACK &&
                    !integration.link &&
                    (integration.status === IntegrationStatus.NOT_ACTIVATED || integration.status === IntegrationStatus.DISABLED) &&
                    (isImpersonating ? (
                      <Typography>{localeActionMessages['whileImpersonatingError']}</Typography>
                    ) : (
                      <Button variant="contained" color="primary" onClick={connectSlack}>
                        {localeIntegrations['clickToEnable']}
                      </Button>
                    ))}
                  {/* Generate Structure for non-Slack integrations */}
                  {integration.status === IntegrationStatus.ENABLED &&
                    integration.integrationProvider !== IntegrationProvider.SLACK &&
                    (isImpersonating ? (
                      <Typography>{localeActionMessages['whileImpersonatingError']}</Typography>
                    ) : (
                      <Tooltip title={localeIntegrations['generateTooltip']}>
                        <Button
                          variant="contained"
                          color="primary"
                          href=""
                          onClick={() => handleShowGenerateModal(integration)}
                          target="_blank"
                          className="ml-3"
                          disabled={generatingStructure}>
                          {localeIntegrations['clickToGenerateStructure']}
                        </Button>
                      </Tooltip>
                    ))}
                  {/* Slack enabled - no structure generation needed */}
                  {integration.status === IntegrationStatus.ENABLED &&
                    integration.integrationProvider === IntegrationProvider.SLACK && (
                      <Typography variant="body2" color="success.main">
                        {localeIntegrations['integrationDone']}
                      </Typography>
                    )}
                  {/* Generating status - show in progress message */}
                  {integration.status === IntegrationStatus.GENERATING && (
                    <Tooltip title={localeIntegrations['generatingInProgressTooltip']}>
                      <Typography variant="body2" color="warning.main">
                        {localeIntegrations['generatingInProgress']}
                      </Typography>
                    </Tooltip>
                  )}
                  {integration.status === IntegrationStatus.GENERATED && (
                    <Typography variant="body2" color="success.main">
                      {localeIntegrations['enabledGenerated']}
                    </Typography>
                  )}
                  {/* Failed status - show error message */}
                  {integration.status === IntegrationStatus.FAILED && (
                    <Typography variant="body2" color="error.main">
                      {localeIntegrations['generationFailed']}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {(integration.status === IntegrationStatus.ENABLED ||
                    integration.status === IntegrationStatus.GENERATING ||
                    integration.status === IntegrationStatus.GENERATED ||
                    integration.status === IntegrationStatus.FAILED) &&
                    role !== UserRoles.USER && (
                      <Button variant="contained" color="error" onClick={() => handleShowDeleteModal(integration)}>
                        {localeCommon['delete']}
                      </Button>
                    )}
                </TableCell>
              </TableRow>
              </WolfTooltip>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedIntegration && (
        <>
          <CustomModal
            showButtons={true}
            isOpen={showDeleteModal}
            onClose={cancelModal}
            onConfirm={handleConfirmDelete}
            maxWidth="600px"
            title={localeIntegrations['confirmDelete']}
            customConfirmColor="error">
            <DeleteIntegrationModal integration={selectedIntegration} />
          </CustomModal>

          <CustomModal
            showButtons={true}
            title={localeIntegrations['confirmGenerate']}
            isOpen={showGenerateModal}
            maxWidth="600px"
            onClose={cancelModal}
            onConfirm={handleConfirmStructureGenerate}>
            <GenerateStructureIntegrationModal integration={selectedIntegration} />
          </CustomModal>
        </>
      )}
      <GeneratingStructureLoadingModal isOpen={generatingStructure} integrationType={generatingIntegrationType} />
    </Box>
  );
};

export default IntegrationList;
